const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string) ?? 'http://localhost:8787';
const KEY = 'studio.parentToken';

export const getParentToken = (): string | null => {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
};

/**
 * Session storage, not local: the parent console closes when the tab does. A
 * console left signed in on a shared iPad is exactly what it is there to prevent.
 */
export const setParentToken = (token: string): void => {
  try {
    sessionStorage.setItem(KEY, token);
  } catch {
    /* ignore */
  }
};

export const clearParentToken = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getParentToken();
  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (response.status === 401) throw new Error('unauthorized');
  if (!response.ok) throw new Error(`request_failed_${response.status}`);
  return response.json() as Promise<T>;
}

export type KidOverview = {
  id: string;
  name: string;
  profile: string;
  isActive: boolean;
  credits: number;
  minutesUsedThisWeek: number;
  weeklyMinutes: number;
  gamesShipped: number;
  buildsBlocked: number;
};

export type Overview = {
  kids: KidOverview[];
  weekOf: string;
  spend: { monthToDateUsd: number; capUsd: number; monthToDateMicros: number; capMicros: number };
};

export type AuditEntry = {
  id: string;
  kidId: string | null;
  kidName: string | null;
  event: string;
  at: number;
  payload: unknown;
};

export const login = (accessKey: string) =>
  call<{ token: string }>('/api/parent/login', {
    method: 'POST',
    body: JSON.stringify({ accessKey }),
  });

export const getOverview = () => call<Overview>('/api/parent/overview');

export const getAudit = (kidId?: string) =>
  call<AuditEntry[]>(`/api/parent/audit?limit=100${kidId ? `&kidId=${encodeURIComponent(kidId)}` : ''}`);

export const setActive = (kidId: string, isActive: boolean) =>
  call<{ ok: true }>(`/api/parent/kid/${kidId}/active`, {
    method: 'POST',
    body: JSON.stringify({ isActive }),
  });

export const issueDeviceToken = (kidId: string, label: string) =>
  call<{ token: string }>(`/api/parent/kid/${kidId}/device-token`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  });

export const revokeTokens = (kidId: string) =>
  call<{ ok: true }>(`/api/parent/kid/${kidId}/revoke-tokens`, { method: 'POST' });

export const setCap = (capUsd: number) =>
  call<{ ok: true }>('/api/parent/cap', { method: 'POST', body: JSON.stringify({ capUsd }) });
